import { config } from '../../config';
import { MATCH_STATE } from '../machine';
import { ok, reject } from '../result';
import type { DomainLogEntry, MatchNoteRow } from '../../types';
import type {
  Decision,
  HalftimeAdjustmentOp,
  MatchNoteAudioOp,
  MatchNoteOp,
  PromoteHalftimeAdjustmentCommand,
  PromoteHalftimeAdjustmentSnapshot,
  StartMatchNoteAudioCommand,
  StartMatchNoteAudioSnapshot,
  WriteMatchNoteCommand,
  WriteMatchNoteSnapshot,
} from './types';

/**
 * HT-5 and HT-8 — the two halftime activities that write **new artifact rows** (build unit U-13a).
 *
 * ── Why these two share a module, and what that buys ─────────────────────────
 *
 * Neither can touch a `Hypothesis`. `Decision<HalftimeAdjustmentOp>` reaches `talkPoints` only and
 * `Decision<MatchNoteOp>` reaches `matchNotes` only, so **HT-6 and INV-14 are compile errors rather
 * than review findings** — see the two op types for the argument in full. Grouping them makes that
 * shared property one thing a reader can check, instead of two claims in two files.
 *
 * The other three halftime activities (HT-2 board management, PRE-3 add, FLAG-3 promote) *do* mutate
 * the hypothesis set and are U-13b's; they do not live here, and the split is the reason this
 * module's op types can stay this narrow. PRE-3's add is `commands/halftimeManage.ts`; LOCK-6's swap
 * is `commands/board.ts`, because INV-4 puts every `boardPosition` writer in that one file.
 *
 * ── LIVE-9 does not bind at halftime, and that is a ruling, not an inference ──
 *
 * LIVE-9 forbids **requiring** free text *"in an in-play state"*. Halftime is in-match and **not**
 * in-play (§2's grouping split, and `IN_PLAY_STATES` excludes it), and §7.4 is explicit that at
 * halftime *"typing is unrestricted"*. So `WriteMatchNote` accepts text and does not apologise for
 * it — while HT-8's *"either, both, or neither"* means it must equally accept a note with **no**
 * text, which is asserted as its own case rather than left implied.
 */

const MODULE = 'commands/halftime';

/**
 * HT-5 — rewrite the halftime talk-point selection.
 *
 * ── The state gate, and why it is `halftime` exactly ────────────────────────
 *
 * Not `isInMatch`. A halftime adjustment written during the second half would be a `phase=halftime`
 * artifact created after the break it names, and VER-8 asserts halftime rows *"survive untouched
 * through the second half and distillation"* — a rule with no meaning if the second half can still
 * add to them. `preMatch` is out for the same reason from the other side.
 *
 * ── What is *not* checked here, deliberately ───────────────────────────────
 *
 * The `TALKPOINT_COUNT` bound is re-checked below **and** enforced by `repos/talkPoints.writeSelection`
 * inside the transaction. Two checks rather than one because they answer different questions: this
 * one produces a typed rejection the UI can render, and the repo's produces an abort that holds even
 * for a caller that never came through this handler (an import, §11.9). INV-6's *"unconstrained from
 * 0 to 3"* is a **bound, not a target** — there is no minimum here, and there must not be, because
 * §7.3 says zero is legal and *"there is no exit nag at zero."*
 */
export function handlePromoteHalftimeAdjustment(
  cmd: PromoteHalftimeAdjustmentCommand,
  snap: PromoteHalftimeAdjustmentSnapshot,
): Decision<HalftimeAdjustmentOp> {
  const match = snap.match;
  if (match === undefined) {
    return reject(MODULE, cmd.matchId, 'ERR_MATCH_NOT_FOUND', 'That match no longer exists.');
  }

  if (match.state !== MATCH_STATE.halftime) {
    return reject(
      MODULE,
      cmd.matchId,
      'ERR_NOT_AT_HALFTIME',
      'Halftime adjustments can only be changed during the break.',
      { state: match.state },
    );
  }

  if (cmd.ordered.length > config.TALKPOINT_COUNT) {
    return reject(
      MODULE,
      cmd.matchId,
      'ERR_TALKPOINT_LIMIT',
      `You can take at most ${String(config.TALKPOINT_COUNT)} adjustments into the second half.`,
      { count: cmd.ordered.length },
    );
  }

  const known = new Set(snap.hypothesisIds);
  for (const entry of cmd.ordered) {
    if (entry.matchId !== cmd.matchId || entry.phase !== 'halftime') {
      return reject(
        MODULE,
        cmd.matchId,
        'ERR_COMMAND_ROW_MISMATCH',
        'That adjustment does not belong to this match.',
        { id: entry.id, phase: entry.phase },
      );
    }
    // A talk point may legitimately have no source (a free-typed instruction), but a *stated* source
    // must exist — CARD-1 renders from it, and a dangling id is a blank line on the share card with
    // nothing anywhere reporting why.
    if (entry.sourceHypothesisId !== null && !known.has(entry.sourceHypothesisId)) {
      return reject(
        MODULE,
        cmd.matchId,
        'ERR_HYPOTHESIS_NOT_FOUND',
        'That pattern is no longer on this match.',
        { id: entry.id, sourceHypothesisId: entry.sourceHypothesisId },
      );
    }
  }

  const ids = cmd.ordered.map((entry) => entry.id);
  if (new Set(ids).size !== ids.length) {
    return reject(
      MODULE,
      cmd.matchId,
      'ERR_TALKPOINT_DUPLICATE',
      'The same adjustment was selected twice.',
      { ids },
    );
  }

  return ok({
    ops: [
      { t: 'writeTalkPointSelection', matchId: cmd.matchId, phase: 'halftime', ordered: cmd.ordered },
    ],
    log: [
      {
        module: MODULE,
        level: 'info',
        matchId: cmd.matchId,
        msg: 'HT-5: halftime adjustment selection written',
        data: { count: cmd.ordered.length, was: snap.existingHalftime.length },
      },
    ],
    // No postcondition. `talkPointSet` asserts INV-6's *exactly*-`TALKPOINT_COUNT` rule, which
    // §17.6 says holds against `phase=fullTime` rows **only** — running it here would abort the
    // legal zero- and one-adjustment cases §7.3 names as normal.
    post: [],
  });
}

/**
 * HT-8 — write or edit the halftime note.
 *
 * ── The three shapes a note may take ───────────────────────────────────────
 *
 * *"text field + record button; either, both, or neither."* All three are accepted:
 * `{text, audioBlobId}` may be `{set, null}`, `{null, set}` or `{set, set}`. The fourth —
 * `{null, null}` — is refused on **create** and accepted on **edit**, because on create it is a row
 * that says nothing and on edit it is the user clearing a note they no longer want. Refusing the
 * edit case would leave a typed note undeleteable from this surface.
 *
 * ── The minute is not recomputed, and that is SES-10 ───────────────────────
 *
 * The row arrives with `matchMinute` already derived (`= halfLengthMin` at halftime). A handler that
 * recomputed it would be deriving at handler time the value SES-10 fixes at creation time — the same
 * defect §8.2 corrects for audio observations, where a stop-time derivation filed a 44′ flag as a
 * halftime one.
 */
export function handleWriteMatchNote(
  cmd: WriteMatchNoteCommand,
  snap: WriteMatchNoteSnapshot,
): Decision<MatchNoteOp> {
  const refusal = noteWindowProblem<MatchNoteOp>(cmd.matchId, cmd.row, snap.match);
  if (refusal !== null) return refusal;

  const plan = planMatchNoteWrite(cmd.row, snap.existing);

  if (snap.existing === undefined && plan.text === null && cmd.row.audioBlobId === null) {
    return reject(MODULE, cmd.matchId, 'ERR_NOTE_EMPTY', 'There is nothing in that note to save.', {
      id: cmd.row.id,
    });
  }

  return ok({
    ops: plan.ops,
    log: [noteWritten(cmd.matchId, cmd.row, plan, snap.existing !== undefined)],
    post: [],
  });
}

/**
 * HT-8's **voice** start — *"text field + record button; either, both, or neither"* (build unit
 * U-13c).
 *
 * ── The one thing this adds over `handleWriteMatchNote` ──────────────────────
 *
 * `openAudioBlob`, in the same transaction. Everything else — the halftime window, the
 * row-belongs-here check, the text normalisation, the insert-vs-update decision — is the *same*
 * effect, produced by the *same* two functions, because a second recording entry that re-derived
 * them would be free to disagree with the first (`new-path-parity-single-source-the-effect`). The
 * one that would have bitten: `''` and `null` both mean "no text", and a voice start that stored
 * `''` would make INV-12's byte-determinism archive two different bytes for one state.
 *
 * ── `audioBlobId` must be non-null, and the refusal is the requirement ───────
 *
 * §8.2 mints the id *before* this command is dispatched and `MediaRecorder.start` runs *after* it
 * commits. A voice start carrying `null` therefore means the caller either skipped the mint or took
 * FLAG-4's silent branch — and on **this** subject a silent branch has nothing to file: a
 * `MatchNote` with no text and no audio is a row that says nothing, which `ERR_NOTE_EMPTY` already
 * refuses on create. FLAG-4's *"something happened at minute 34"* value does not transfer, because a
 * halftime note carries no moment of its own (SES-10 fixes every halftime minute at
 * `halfLengthMin`). So the honest answer is to write nothing and let the surface say the microphone
 * is off — never a note that claims to hold a recording it does not have.
 *
 * ── `ERR_NOTE_EMPTY` is unreachable from here, and that is structural ────────
 *
 * A non-null `audioBlobId` is required above, so the emptiness test `handleWriteMatchNote` runs on
 * create can never fire on this path — which is why it is not repeated here rather than copied and
 * left as dead defence.
 */
export function handleStartMatchNoteAudio(
  cmd: StartMatchNoteAudioCommand,
  snap: StartMatchNoteAudioSnapshot,
): Decision<MatchNoteAudioOp> {
  const refusal = noteWindowProblem<MatchNoteAudioOp>(cmd.matchId, cmd.row, snap.match);
  if (refusal !== null) return refusal;

  const audioBlobId = cmd.row.audioBlobId;
  if (audioBlobId === null) {
    return reject(
      MODULE,
      cmd.matchId,
      'ERR_COMMAND_ROW_MISMATCH',
      'That voice note has nowhere to record to.',
      { id: cmd.row.id },
    );
  }

  const plan = planMatchNoteWrite(cmd.row, snap.existing);

  return ok({
    // The blob row first, so no read inside this transaction can see a note pointing at a row the
    // transaction has not created yet. Both writes commit together or neither does (§8.2 step 1).
    ops: [{ t: 'openAudioBlob', id: audioBlobId, createdAt: snap.now }, ...plan.ops],
    log: [noteWritten(cmd.matchId, cmd.row, plan, snap.existing !== undefined)],
    post: [],
  });
}

// ── The shared halves of the two note paths ──────────────────────────────────

/**
 * The window both note commands answer to, or `null` when they may proceed.
 *
 * Generic in the op type so one implementation serves both handlers: `reject` is already generic,
 * and duplicating three guards across two entry points is exactly how the second path acquires a
 * subtly different window.
 *
 * The state gate is `halftime` **exactly**, not `isInMatch`: a note written during the second half
 * would be a `phase='halftime'` artifact created after the break it names, and VER-8 asserts
 * halftime rows *"survive untouched through the second half"* — a claim with no meaning if the
 * second half can still add to them.
 */
function noteWindowProblem<Op extends MatchNoteOp | MatchNoteAudioOp>(
  matchId: string,
  row: MatchNoteRow,
  match: WriteMatchNoteSnapshot['match'],
): Decision<Op> | null {
  if (match === undefined) {
    return reject(MODULE, matchId, 'ERR_MATCH_NOT_FOUND', 'That match no longer exists.');
  }
  if (match.state !== MATCH_STATE.halftime) {
    return reject(
      MODULE,
      matchId,
      'ERR_NOT_AT_HALFTIME',
      'A halftime note can only be written during the break.',
      { state: match.state },
    );
  }
  if (row.matchId !== matchId || row.phase !== 'halftime') {
    return reject(
      MODULE,
      matchId,
      'ERR_COMMAND_ROW_MISMATCH',
      'That note does not belong to this match.',
      { id: row.id, phase: row.phase },
    );
  }
  return null;
}

interface MatchNotePlan {
  readonly ops: readonly MatchNoteOp[];
  /** The normalised text the ops will actually store, so the log reports what was written. */
  readonly text: string | null;
}

/**
 * INV-14's insert-or-update, and **one representation of "no text"**.
 *
 * Trimming alone yields `''` for a cleared note, which is a *second* way to say the same thing —
 * and the two diverge everywhere it matters: `row.text !== null` reads `''` as a note that exists,
 * INV-12's byte-determinism would archive two different bytes for one state, and CARD-1 would render
 * an empty line for a note the user deleted. Normalising here means the column carries the meaning
 * rather than the caller's typing history — and it does so for *both* entry points, which is the
 * reason this is a function and not two copies.
 *
 * The update arm never carries `matchMinute`: `updateMatchNote`'s change type cannot express one, so
 * an edit — by either path — has no way to move a note in time (INV-7, SES-10).
 */
function planMatchNoteWrite(row: MatchNoteRow, existing: MatchNoteRow | undefined): MatchNotePlan {
  const trimmed = row.text === null ? '' : row.text.trim();
  const text = trimmed === '' ? null : trimmed;
  return {
    text,
    ops:
      existing === undefined
        ? [{ t: 'insertMatchNote', row: { ...row, text } }]
        : [{ t: 'updateMatchNote', id: row.id, changes: { text, audioBlobId: row.audioBlobId } }],
  };
}

/** OBS-1's entry for either path, so "what was written" is reported the same way by both. */
function noteWritten(
  matchId: string,
  row: MatchNoteRow,
  plan: MatchNotePlan,
  edited: boolean,
): DomainLogEntry {
  return {
    module: MODULE,
    level: 'info',
    matchId,
    msg: edited ? 'HT-8: halftime note updated' : 'HT-8: halftime note created',
    data: {
      id: row.id,
      hasText: plan.text !== null,
      hasAudio: row.audioBlobId !== null,
      matchMinute: row.matchMinute,
    },
  };
}
